import React, { useState, useCallback } from "react";
import { useAPIUpload, usePost } from "../../API";
import {
  Button,
  useToast,
  Modal,
  Alert,
  Spinner,
  Input,
  Select,
  Checkbox,
  FileInput,
  Badge,
} from "@aidkitorg/component-library";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { snakeToEnglish } from "../../Util";

interface DocumentUploadProps {
  onUploadComplete?: () => void;
  categories?: string[];
}

interface UploadState {
  uploading: boolean;
  progress: number;
  error: string | null;
}

export function DocumentUpload({ onUploadComplete, categories = [] }: DocumentUploadProps) {

  const [file, setFile] = useState<File | undefined>();
  const [category, setCategory] = useState<{ label: string | JSX.Element, value: string } | null>(null);
  const [customCategory, setCustomCategory] = useState<string>('');
  const [isTemplate, setIsTemplate] = useState<boolean>(false);
  const [uploadState, setUploadState] = useState<UploadState>({
    uploading: false,
    progress: 0,
    error: null
  });
  const [showModal, setShowModal] = useState(false);

  const registerDocument = usePost('/ai/document_library/register');
  const uploadURL = usePost("/document/upload_url");
  const upload = useAPIUpload("/upload", (progress: number) => {
    setUploadState({
      uploading: progress >= 100,
      progress: Math.round(progress * 100),
      error: null
    });
  });
  const { toast } = useToast();

  const resetForm = useCallback(() => {
    setFile(undefined);
    setCategory(null);
    setCustomCategory('');
    setIsTemplate(false);
    setUploadState({ uploading: false, progress: 0, error: null });
  }, []);

  const validateMetadata = useCallback((metadataString: string): string | null => {
    try {
      const parsed = JSON.parse(metadataString);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return 'Metadata must be a valid JSON object';
      }
      return null;
    } catch (error) {
      return 'Invalid JSON format';
    }
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = Array.from(event.target.files || [])[0];
    setFile(file);
  }, [setFile]);

  const handleFileDelete = useCallback(() => {
    setFile(undefined);
  }, []);

  const convertFileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file) {
      setUploadState(prev => ({ ...prev, error: 'Please select at least one file to upload' }));
      return;
    }

    const finalCategory = category?.value === 'custom' ? customCategory.trim() : category?.value;
    if (!finalCategory) {
      setUploadState(prev => ({ ...prev, error: 'Please specify a category' }));
      return;
    }

    try {

      const { uploadURL: signedUploadUrl, savedPath: url } = await uploadURL({
        path: file.name,
        length: file.size,
      });

      await upload([file], {}, signedUploadUrl, 'PUT');

      const response = await registerDocument({
        filePath: url!,
        category: finalCategory,
        template: isTemplate
      });

      setUploadState(prev => ({ ...prev, progress: 90 }));

      if (response.success) {
        setUploadState({ uploading: false, progress: 100, error: null });
        toast({ description: 'Document uploaded successfully!', variant: 'success' });
        resetForm();
        setShowModal(false);
        onUploadComplete?.();
      } else {
        throw new Error(response.error?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setUploadState({ uploading: false, progress: 0, error: errorMessage });
      toast({ description: `Upload failed: ${errorMessage}`, variant: 'error' });
    }
  }, [file, category, customCategory, isTemplate, registerDocument, toast, resetForm, onUploadComplete, validateMetadata, convertFileToBase64]);

  const handleCancel = useCallback(() => {
    if (!uploadState.uploading) {
      resetForm();
      setShowModal(false);
    }
  }, [uploadState.uploading, resetForm]);

  const showCustomCategory = category?.value === 'custom';
  const canUpload = file && !uploadState.uploading && (category?.value !== 'custom' ? category : customCategory.trim());

  const categoryOptions = [
    { value: '', label: 'Select a category...' },
    ...categories.map(cat => ({ value: cat, label: cat })),
    { value: 'custom', label: 'Custom Category...' }
  ];

  return (
    <>
      <Button variant="primary" onClick={() => setShowModal(true)}>
        Upload Document
      </Button>

      <Modal
        open={showModal}
        onClose={handleCancel}
        title="Upload Document to Library"
        size="lg"
      >
        <div className="space-y-6">

          {uploadState.error && (
            <Alert variant="danger" className="mb-4">
              {uploadState.error}
            </Alert>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select File <span className="text-red-500">*</span>
              </label>
              <FileInput
                single
                files={file ? [file] : []}
                onChange={handleFileChange}
                onDeleteHandler={handleFileDelete}
                className="mb-2"
              />
              <p className="text-sm text-gray-500">
                Only PDFs are Supported. Maximum size: 10MB.
              </p>
            </div>

            <div>
              <Select
                label="Category"
                description=""
                value={category ?? { label: '', value: '' }}
                onChange={setCategory}
                options={categoryOptions}
                disabled={uploadState.uploading}
                placeholder="Select a category..."
              />
            </div>

            {showCustomCategory && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Category <span className="text-red-500">*</span>
                </label>
                <Input
                  label="Custom Category"
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  placeholder="Enter custom category name"
                  disabled={uploadState.uploading}
                  showLabel={false}
                />
              </div>
            )}

            <div>
              <Checkbox
                label="Mark as Template"
                id="is-template"
                checked={isTemplate}
                onChange={(e) => setIsTemplate(!!e)}
                disabled={uploadState.uploading}
              />
              <p className="text-sm text-gray-500 mt-1">
                Template documents can be used as starting points for generating new documents.
              </p>
            </div>

            {
              uploadState.uploading && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Uploading...</span>
                    <span className="text-sm">{uploadState.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadState.progress}%` }}
                    />
                  </div>
                </div>
              )
            }
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={handleCancel}
              isDisabled={uploadState.uploading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              isDisabled={!canUpload}
            >
              {uploadState.uploading ? (
                <div className="flex items-center space-x-2">
                  <Spinner size="small" />
                  <span>Uploading...</span>
                </div>
              ) : (
                'Upload Document'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
